import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Input } from 'reactstrap';

import { createProductSurcharge, deleteProductSurcharge, fetchProductSurcharges, updateProductSurcharge } from '../../actions/productSurcharges';
import { Button, InfoBanner, Loader } from '../commons/utils';
import { appliedOn, surchargeTypes, surgeValueType } from './utils';
import ProductSurchargeCards from './segments/productSurchargeCards';
import AddAndEditSurcharge from './modals/addAndEditSurcharge';
import DeleteConfirmation from './modals/deleteConfirmation';
import { showNotification } from '../toastNotification/toastNotification';

const ProductSurcharges = ({ t, surcharges, fetchProductSurcharges, isAdmin, createProductSurcharge, updateProductSurcharge, deleteProductSurcharge }) => {
    const [openDataModal, setOpenDataModal] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState({ isOpen: false, id: null });
    const [selectedSurcharge, setSelectedSurcharge] = useState({
        name: '',
        surchargeType: surchargeTypes[0]?.type,
        value: 0,
        valueType: surgeValueType[0]?.type,
        appliedOn: appliedOn[0]?.value,
        externalCode: '',
    });
    const [filterData, setFilterData] = useState(0);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        Promise.all([fetchProductSurcharges()]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // if surcharges are still being fetched, show the loader
    if (surcharges.isFetching) {
        return <Loader message={t('surcharge:fetchingSurcharges')} />;
    }

    /**
     * When input is changed on the create/edit tax form
     * @param {*} type string
     * @param {*} value string | int
     */
    const handleDataChange = (type, value) => {
        let inputValue = value;
        let vatData = {};

        if (type !== 'name' && type !== 'appliedOn' && type !== 'externalCode') {
            if (type === 'value' && value === '') {
                inputValue = '';
            } else if (type === 'value') {
                // Keep as raw string during typing to allow decimals like "1.0" -> "1.05"
                // Only strip leading zeros when not a decimal (e.g. "05" -> "5" but keep "0.5")
                if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
                    inputValue = value.replace(/^0+/, '') || '0';
                } else {
                    inputValue = value;
                }
            } else {
                inputValue = Number(value);
            }
        }

        if (type === 'value' && inputValue !== '' && Number(inputValue) < 0) {
            inputValue = '0';
        }
        if (type === 'name' && value && value.length > 50) {
            return;
        }
        if (type === 'externalCode' && value && value.length > 100) {
            return;
        }

        // handle the case of VAT Separately
        if (type === 'surchargeType' && inputValue === 1) {
            vatData = {
                valueType: 1,
            };
        }
        setSelectedSurcharge((prev) => ({ ...prev, [type]: inputValue, ...(vatData.valueType && vatData) }));
    };

    const handleValueBlur = () => {
        const val = selectedSurcharge.value;
        if (val === '' || val === null || val === undefined) {
            setSelectedSurcharge((prev) => ({ ...prev, value: '0' }));
        } else {
            // Normalize on blur: "1.0" -> "1", "05" -> "5", "1.50" -> "1.5"
            const num = Number(val);
            if (!isNaN(num)) {
                setSelectedSurcharge((prev) => ({ ...prev, value: num.toString() }));
            }
        }
    };

    /**
     * saving the data
     */
    const saveSurchargeData = () => {
        if (selectedSurcharge.id) {
            const surchargeToUpdate = { ...selectedSurcharge, value: Number(selectedSurcharge.value) || 0 };
            delete surchargeToUpdate.organizationId;
            delete surchargeToUpdate.id;
            // delete isFromVat from request body for old migrated VAT surcharge
            delete surchargeToUpdate?.isFromVat;

            updateProductSurcharge(selectedSurcharge.id, surchargeToUpdate)
                .then(showNotification(t('surcharge:taxes'), t('surcharge:requestSuccess'), 'success'))
                .catch(() => showNotification(t('surcharge:taxes'), t('surcharge:requstFailed'), 'error'));
        } else {
            createProductSurcharge({ ...selectedSurcharge, value: Number(selectedSurcharge.value) || 0 })
                .then(showNotification(t('surcharge:taxes'), t('surcharge:requestSuccess'), 'success'))
                .catch(() => showNotification(t('surcharge:taxes'), t('surcharge:requstFailed'), 'error'));
        }
        setSelectedSurcharge({
            name: '',
            surchargeType: surchargeTypes[2]?.type,
            value: 0,
            valueType: surgeValueType[1]?.type,
            appliedOn: appliedOn[1]?.value,
        });
        setOpenDataModal(false);
    };

    const deleteSurchargeData = () => {
        if (!confirmationModal.id) return;
        deleteProductSurcharge(confirmationModal.id)
            .then(() => {
                fetchProductSurcharges();
                showNotification(t('surcharge:taxes'), t('surcharge:requestSuccess'), 'success');
            })
            .catch(() => showNotification(t('surcharge:taxes'), t('surcharge:requstFailed'), 'error'));
        setConfirmationModal({ isOpen: false, id: null });
    };

    return (
        <div className="surcharges">
            <AddAndEditSurcharge
                isOpen={openDataModal}
                selectedSurcharge={selectedSurcharge}
                save={() => saveSurchargeData()}
                cancel={() => {
                    setSelectedSurcharge({
                        name: '',
                        surchargeType: surchargeTypes[2]?.type,
                        value: 0,
                        valueType: surgeValueType[1]?.type,
                        appliedOn: appliedOn[1]?.value,
                    });
                    setOpenDataModal(false);
                }}
                dataChange={handleDataChange}
                onValueBlur={handleValueBlur}
                t={t}
            />
            <DeleteConfirmation
                isOpen={confirmationModal.isOpen}
                confirmDelete={() => deleteSurchargeData()}
                cancel={() => setConfirmationModal({ isOpen: false, id: null })}
                t={t}
            />
            <div className="surcharges__search">
                <Input type="select" className="surcharges__search-select" onChange={(e) => setFilterData(e.target.value)}>
                    <option value={0}>{t('all')}</option>
                    {surchargeTypes.map((surchage) => (
                        <option value={surchage.type} key={surchage.name}>
                            {t(`surcharge:${surchage.name}`)}
                        </option>
                    ))}
                </Input>
                <div className="surcharges__search-box">
                    <Input placeholder={t('surcharge:searchTaxes')} onChange={(e) => setSearchText(e.target.value.trim())} />
                    <svg className="surchages__search-icon">
                        <use xlinkHref="#icon-search" />
                    </svg>
                </div>
                <Button type="add" text={t('surcharge:addNewTax')} onClick={() => setOpenDataModal(true)} disabled={!isAdmin}/>
            </div>

            <div className="surcharges__contents">
                {(() => {
                    let allSurcharges = surcharges.productSurcharges || [];

                    if (Number(filterData)) {
                        allSurcharges = allSurcharges.filter((surcharge) => surcharge.surchargeType === Number(filterData));
                    }
                    if (searchText) {
                        allSurcharges = allSurcharges.filter((surcharge) => surcharge.name.toLowerCase().includes(searchText.toLowerCase()));
                    }

                    if (!allSurcharges.length) {
                        return <InfoBanner message={t('surcharge:noSurcharge')} className="surcharges__contents-info" />;
                    }

                    return allSurcharges.map((surcharge, index) => (
                        <ProductSurchargeCards
                            isAdmin={isAdmin}
                            surcharge={surcharge}
                            editSurcharge={(selection) => {
                                setSelectedSurcharge(selection);
                                setOpenDataModal(true);
                            }}
                            deleteSurcharge={(selection) => {
                                setConfirmationModal({ isOpen: true, id: selection.id });
                            }}
                            key={index}
                            t={t}
                        />
                    ));
                })()}
            </div>
        </div>
    );
};

export default connect(
    (state) => ({
        surcharges: state.productSurcharges,
        isAdmin: state && state.user && state.user.isAdmin,
    }),
    {
        fetchProductSurcharges,
        createProductSurcharge,
        updateProductSurcharge,
        deleteProductSurcharge,
    },
)(ProductSurcharges);
